/* tslint:disable:max-file-line-count */
import { action, computed, extendObservable, observable, runInAction, transaction } from 'mobx';

import { AnalyticsStore, ContextStore, SerializedData, Store } from 'bernie-core';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { PageData } from 'bernie-http';

import { BexApiSource } from 'sources/bex-api-source';

import { adaptPropertySearch, getPropertySearchInput, mapOmnitureData, mapPropertySearchResults } from 'stores/adapters';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  CarouselModel,
  CustomerInterest,
  HotelResultsViewModel,
  HotelViewModel,
  LoyaltyEducation,
  LoyaltyMessagingModel,
  OverFilteredModel,
  PriceBreakDownDetailsViewModel,
  PrimaryPageMessagingModel,
  ReferrerInfoModel,
  RegionInfoModel,
  RewardsDialogModel,
  SharedGuidsModel
} from 'models';
import { ExitIntentModel } from '/models/common/common-types';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { interceptGraphQLResponse } from './dev-modification/intercept';
import { LodgingPwaPropertySearch, PaginationInput, Points, SearchDialogMessage, SearchLinkMessage } from '__generated__/api';
import { MessagingCardProps } from 'src/common/components/common/messaging-card/messaging-card.tsx';
import { OperationResult } from '__generated__/api/api-operations';
import { stringify } from 'query-string';

export class SearchResultsStore extends Store {
  public static INITIAL_HOTEL_RENDER_COUNT = 20;
  public static HOTEL_TOTAL_RENDER_COUNT = 50;

  constructor(private bexApiSource: BexApiSource, state: SerializedData, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.searchCriteria = new SearchCriteriaModel();

    this.fetch = this.fetch.bind(this);
    this.searchProperties = this.searchProperties.bind(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    this.syncSearchDestination();
    delete data.searchCriteria;

    extendObservable(this, data);
  }

  public static DEFAULTS = {
    alternateDateFetchUrl: undefined,
    exitIntent: undefined,
    expandForm: false,
    foregoClientFetch: false,
    hotels: [],
    hotelsMaxDisplay: SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT,
    insights: undefined,
    isLoading: true,
    lodgingPriceBreakDown: {},
    loyaltyMessagingModel: undefined,
    messages: [],
    resultSectionHeadings: [],
    omnitureData: {},
    overFilteredModel: undefined,
    pageData: {},
    primaryPageMessagingModel: undefined,
    searchAndFilterDisplayOptions: {},
    searchedDestination: '',
    shouldFetch: true,
    sortAndFilter: {},
    totalPropertyCount: 0,
    carouselListings: []
  };

  @observable
  public alternateDateFetchUrl: string;
  @observable
  public error: any;
  @observable
  public errors: any[];
  @observable
  public exitIntent: ExitIntentModel;
  @observable
  public expandForm: boolean; // Set on client.
  @observable
  public foregoClientFetch: boolean;
  @observable
  public hotels: HotelViewModel[];
  @observable
  public hotelsMaxDisplay: number;
  @observable
  public insights: any;
  @observable
  public isLoading: boolean;
  @observable
  public loyaltyEducation: LoyaltyEducation;
  @observable
  public loyaltyMessagingModel: LoyaltyMessagingModel;
  @observable
  public lodgingPriceBreakDown: PriceBreakDownDetailsViewModel;
  @observable
  public map: any;
  @observable
  public messages: MessagingCardProps[];
  @observable
  public resultSectionHeadings: LodgingPwaPropertySearch.ResultSectionHeadings[];
  @observable
  public omnitureData: any;
  @observable
  public overFiltered: boolean;
  @observable
  public overFilteredModel: OverFilteredModel;
  @observable
  public primaryPageMessagingModel: PrimaryPageMessagingModel;
  @observable
  public propertyTypeCarousel: CarouselModel;
  @observable
  public referrerInfo: ReferrerInfoModel;
  @observable
  public regionInfo: RegionInfoModel;
  @observable
  public rewardsDialogContent: RewardsDialogModel;
  @observable
  public searchAndFilterDisplayOptions: any; // Set on server.
  @observable
  public searchedDestination: string;
  @observable
  public sharedGuids: SharedGuidsModel;
  @observable
  public shouldFetch: boolean;
  @observable
  public sortAndFilter: any;
  @observable
  public resultsSummary: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
  @observable
  public totalPropertyCount: number;
  @observable
  public carouselListings: CarouselModel[];
  @observable.ref
  public pageData: PageData;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;
  @observable
  public multiItemSearchContext: LodgingPwaPropertySearch.MultiItemSearchContext;

  public defaultRequestFields: any;
  public bexApi: number = 0;
  public maxHotelsDisplayedPerLoad: number = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  public prefetchedMultiItemSession: boolean = false;

  public isBexApiRollout: boolean = false;

  @computed
  get isError(): boolean {
    return Boolean(!this.isLoading && (this.error || this.errors || this.overFiltered));
  }

  @computed
  get isEmpty(): boolean {
    return Boolean(!this.isLoading && (!this.hotels || this.hotels.length === 0));
  }

  @action
  public showMoreHotels(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItem: MultiItemStore
  ): Promise<number> {
    this.isLoading = true;

    return new Promise(resolve => {
      setTimeout(
        action(() => {
          const currentMax = this.hotelsMaxDisplay;
          const numHotelsFetched = this.hotels.length;

          if (this.hotelsMaxDisplay < numHotelsFetched) {
            this.hotelsMaxDisplay += this.maxHotelsDisplayedPerLoad;
          }

          if (
            this.hotelsMaxDisplay + this.maxHotelsDisplayedPerLoad >= numHotelsFetched && // is one batch away from the end
            numHotelsFetched < this.totalPropertyCount
          ) {
            this.fetchNextBatch(analyticsStore, context, customerInterests, numHotelsFetched, this.maxHotelsDisplayedPerLoad, multiItem);
          } else {
            this.isLoading = false;
          }

          resolve(currentMax);
        }),
        400
      );
    });
  }

  private fetchNextBatch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    startingIndex: number,
    numToFetch: number,
    multiItem: MultiItemStore
  ): void {
    const multiItemContext = multiItem ? multiItem.multiItemContext : null;

    this.searchProperties(context, customerInterests, multiItemContext, this.searchCriteria, {
      size: numToFetch,
      startingIndex
    })
      .then(({ data, errors, extensions }) => {
        transaction(() => {
          this.errors = errors;
          // isLoading needs to change with new omniture data in one step
          this.isLoading = false;
          this.hotels = this.hotels.concat(mapPropertySearchResults(data));
          this.omnitureData = mapOmnitureData(extensions.analytics);

          if (multiItem) {
            multiItem.updateSessionId(this.getMultiItemId(data));
          }
        });
      })
      .catch(error => {
        this.isLoading = false;
        this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
      });

    analyticsStore.publishClientSidePayload([this.omnitureData]);
  }

  @action
  public updateSearchCriteria(searchCriteria): void {
    this.searchCriteria = searchCriteria;
    this.propertyTypeCarousel = undefined;
    this.syncSearchDestination();
    this.shouldFetch = true;
  }

  @action
  public forceFetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    additionalCriteria: any = {},
    multiItem: MultiItemStore
  ): Promise<any> {
    this.shouldFetch = true;

    return this.fetch(analyticsStore, context, [], additionalCriteria, multiItem);
  }

  @action
  public createSessionBeforeMultiItemFetch(
    multiItem: MultiItemStore,
    context: ContextStore,
    incomingSearchCriteria: SearchCriteriaModel
  ): Promise<any> {
    this.isLoading = true;
    this.shouldFetch = false;
    this.hotels = [];
    this.prefetchedMultiItemSession = true;

    return multiItem
      .createMultiItemSession(context, incomingSearchCriteria)
      .then(misId => (incomingSearchCriteria.misId = misId))
      .catch(() => {
        this.isLoading = false;
        this.shouldFetch = true;
        this.prefetchedMultiItemSession = false;
        this.error = true;
      });
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    additionalCriteria: SerializedData = {},
    multiItem: MultiItemStore
  ): Promise<any> {
    if (this.shouldFetch === false && !this.prefetchedMultiItemSession) {
      return Promise.resolve(this);
    }

    this.prefetchedMultiItemSession = false;
    this.shouldFetch = false;
    this.isLoading = true;
    this.error = null;
    this.errors = [];
    this.hotels = [];
    this.insights = undefined;
    this.messages = [];
    this.carouselListings = [];

    const multiItemContext = multiItem ? multiItem.multiItemContext : null;

    const criteriaJson = this.searchCriteria.toJSON();
    const bexApiCriteria = { ...criteriaJson, ...additionalCriteria };

    return this.searchProperties(context, customerInterests, multiItemContext, bexApiCriteria)
      .then(bexApiResults => {
        if (multiItem && bexApiResults) {
          multiItem.updateSessionId(this.getMultiItemId(bexApiResults.data));
        }
        // update useRewards based on the default value used in Bex api
        if (this.searchCriteria.useRewards === '') {
          this.searchCriteria.useRewards = SearchResultsStore.GetDefaultUseRewards(bexApiResults);
        }

        this.errors = bexApiResults.errors;

        return adaptPropertySearch(bexApiResults);
      })
      .then((data: HotelResultsViewModel) =>
        runInAction('successfully updating search results', () => {
          this.isLoading = false;

          if (data) {
            this.setOverFilteredModel(data.overFilteredModel);

            const searchCriteria = this.searchCriteria.clone();
            const defaultRequestFields = data.defaultRequestFields;

            if (multiItem && multiItem.isPackageShoppingPath() && data.multiItemSearchContext) {
              Object.assign(searchCriteria, {
                multiItemCarPickUpTime: multiItem.getCompressedCarTime(data.multiItemSearchContext, 'pickUpDateTime'),
                multiItemCarDropOffTime: multiItem.getCompressedCarTime(data.multiItemSearchContext, 'dropOffDateTime')
              });
            }

            if (defaultRequestFields) {
              Object.assign(searchCriteria, defaultRequestFields);
              //hack to prevent the destination in the url from getting updated. This causes the back button to not work correctly
              delete defaultRequestFields.destination;
            }

            const sortAndFilterSelected = data.sortAndFilter && data.sortAndFilter.selected;
            if (sortAndFilterSelected) {
              Object.assign(searchCriteria, sortAndFilterSelected);
            }

            if (data.exitIntent) {
              this.exitIntent = data.exitIntent;
            }

            this.searchCriteria = searchCriteria;

            Object.assign(this, data);
            this.setIntentMediaParams(data.intentMedia);

            analyticsStore.publishClientSidePayload([this.omnitureData]);
          }

          return data;
        })
      )
      .catch(err =>
        runInAction('error during search results fetch', () => {
          this.logErrorEvent(err);
          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;
          this.isLoading = false;
          this.shouldFetch = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  private searchProperties(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext,
    combinedCriteria: any,
    searchPagination?: PaginationInput
  ): Promise<OperationResult<LodgingPwaPropertySearch.Query>> {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      combinedCriteria,
      this.sortAndFilter,
      multiItemContext,
      false,
      searchPagination
    );

    return this.bexApiSource.searchProperties(variables).then(interceptGraphQLResponse);
  }

  private syncSearchDestination() {
    if (this.searchCriteria.destination && this.searchCriteria.destination !== '') {
      this.searchedDestination = this.searchCriteria.destination;
    } else if (this.searchedDestination && this.searchedDestination !== '') {
      this.searchCriteria.destination = this.searchedDestination;
    }
  }

  private logErrorEvent(err) {
    if (this.logger) {
      this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
    }
  }

  private setOverFilteredModel(overFilteredModel: OverFilteredModel) {
    if (this.overFilteredModel && !overFilteredModel) {
      this.overFilteredModel = null;
    }
  }

  public findHotel(hotelId: string): HotelViewModel {
    return this.hotels.find(h => h.id && h.id.toString() === hotelId);
  }

  public setIntentMediaParams(intentMedia) {
    if (intentMedia) {
      const {
        city,
        countryCode,
        machineId,
        machineIdHashed,
        publisherSessionId,
        publisherSessionIdHashed,
        publisherUserIdHashed,
        referrerChannel,
        referrerSource,
        state
      } = intentMedia;
      this.sharedGuids = { machineId, machineIdHashed, publisherSessionId, publisherSessionIdHashed, publisherUserIdHashed };
      this.regionInfo = { city, state, countryCode };
      this.referrerInfo = { referrerChannel, referrerSource };
    }
  }

  @action
  public displaySearchButton() {
    this.map = Object.assign({}, this.map, {
      showSearchButton: true
    });
  }

  @action
  public dismissMapMessage() {
    this.map = Object.assign({}, this.map, {
      mapMessage: ''
    });
  }

  @action
  public clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }

  @action
  public setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }

  @action
  public clearFilters(routeStore): void {
    if (routeStore && typeof routeStore.updateSearch === 'function' && this.sortAndFilter && this.sortAndFilter.pills) {
      const currentParams = { ...routeStore.queryParams };
      this.sortAndFilter.pills.forEach(pill => {
        currentParams[pill.name] = '';
      });
      routeStore.updateSearch(stringify(currentParams));
    }
  }

  private getMultiItemId(data: LodgingPwaPropertySearch.Query): string {
    return (
      data &&
      data.propertySearch &&
      data.propertySearch.shoppingContext &&
      data.propertySearch.shoppingContext.multiItem &&
      data.propertySearch.shoppingContext.multiItem.id
    );
  }

  public static GetDefaultUseRewards(bexApiResults: any): string {
    const defaultSwp = bexApiResults?.data?.propertySearch?.searchCriteria?.propertyShopOptions?.points;
    if (Points.SHOP_WITHOUT_POINTS === defaultSwp) {
      return 'false';
    }

    return 'true';
  }
}
